import React, { useState, useRef } from "react"
import { motion } from "framer-motion"
import { Mail, AlertCircle, CheckCircle2 } from "lucide-react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { ScrollReveal } from "../ui/scroll-reveal"
import { getNetlifyFunctionUrl, handleApiResponse } from '../../lib/api-helpers'
import { EventCategory, trackEvent } from '@/lib/analytics'
import { contactClick, createContactButton } from "../../lib/analytics-factory"
import { safeTrackEvent } from "../../lib/analytics-utils"
import { useLocation } from "react-router-dom"
import ReCAPTCHA from "react-google-recaptcha"

// Create a Textarea component based on the Input styling
const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: boolean }
>(({ className, error, ...props }, ref) => {
  return (
    <textarea
      className={`flex w-full rounded-md border ${
        error ? "border-red-500" : "border-input"
      } bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[120px]`}
      ref={ref}
      {...props}
    />
  )
})
Textarea.displayName = "Textarea"

// Define the form data interface
interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  recaptchaToken: string | null;
}

// Helper function to submit contact form
async function submitContactForm(data: ContactFormData) {
  const functionUrl = getNetlifyFunctionUrl('sendEmail')
  
  const response = await fetch(functionUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  
  const result = await handleApiResponse(response)
  
  if (result.success) {
    return { error: null }
  } else {
    return { error: result.error || "Something went wrong" }
  }
}

export function ContactInfo() {
  const location = useLocation();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
    recaptchaToken: null
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{ type: "success" | "error" | null, message: string }>({ 
    type: null, 
    message: "" 
  })
  
  // Form validation
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    subject: false,
    message: false,
    recaptcha: false
  })

  const validate = () => {
    const newErrors = {
      name: !formData.name,
      email: !formData.email || !/^\S+@\S+\.\S+$/.test(formData.email),
      subject: !formData.subject,
      message: !formData.message,
      recaptcha: !formData.recaptchaToken
    }
    
    setErrors(newErrors)
    return !Object.values(newErrors).some(Boolean)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev: ContactFormData) => ({ ...prev, [name]: value }))
    
    // Clear error when field is edited
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: false }))
    }
  }
  
  const handleRecaptchaChange = (token: string | null) => {
    setFormData(prev => ({ ...prev, recaptchaToken: token }))
    if (errors.recaptcha) {
      setErrors(prev => ({ ...prev, recaptcha: false }))
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    // Track form submission attempt
    contactClick('contact_form_submit')(e)
    
    if (!validate()) return
    
    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: "" })
    
    try {
      const { error: responseError } = await submitContactForm(formData)
      
      if (responseError) {
        setSubmitStatus({ 
          type: "error", 
          message: responseError 
        })
        setIsSubmitting(false)
        
        // Track form submission error
        contactClick('contact_form_error')(new Event('error'))
        return
      }
      
      // Clear form
      setFormData({ name: "", email: "", subject: "", message: "", recaptchaToken: null })
      // Reset reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset()
      }
      
      // Show success message
      setSubmitStatus({ 
        type: "success", 
        message: "✅ Thank you! We'll respond shortly." 
      })
      setIsSubmitting(false)
      
      // Track form submission success
      contactClick('contact_form_success')(new Event('success'))
      
      // Reset success message after delay
      setTimeout(() => {
        setSubmitStatus({ type: null, message: "" })
      }, 3000)
    } catch (err) {
      console.error("Error submitting form:", err)
      setSubmitStatus({ 
        type: "error", 
        message: "⚠️ Oops, something went wrong. Please try again or email us at info@syllepsis.io." 
      })
      setIsSubmitting(false)
      
      // Track form submission error
      contactClick('contact_form_error')(new Event('error'))
    }
  }

  const handleEmailClick = () => {
    safeTrackEvent({
      category: EventCategory.Engagement,
      label: 'engagement_contact_email_click',
      context: {
        sourcePage: location.pathname,
        emailAddress: 'info@syllepsis.io'
      }
    });
  };

  return (
    <section className="relative py-16">
      <div className="container relative z-10">
        <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="space-y-6">
              <h2 className="text-xl font-bold">Syllepsis AI Innovation & Knowledge Engineering</h2>
              <div className="space-y-4">
                <p className="text-lg text-muted-foreground">
                  Syllepsis delivers structured AI strategies, rapid Partner AI implementation and embedded AI workflows that transform how your teams work. Reach out today and we'll quickly explore how we can help your organisation.
                </p>
              </div>
              
              <div className="pt-6">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  <a 
                    href="mailto:info@syllepsis.io" 
                    className="text-lg hover:text-primary"
                    onClick={handleEmailClick}
                  >
                    info@syllepsis.io
                  </a>
                </div>
              </div>
            </div>
          </ScrollReveal>
          
          <ScrollReveal delay={0.2}>
            <form onSubmit={handleSubmit} className="space-y-6 bg-primary/5 p-6 rounded-lg border border-primary/10">
              <h3 className="text-xl font-medium mb-4">Send us a message</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? "border-red-500" : ""}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name ? "Name is required" : ""}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Your email address"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? "border-red-500" : ""}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email ? "Please enter a valid email address" : ""}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-1">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    placeholder="What's this about?"
                    value={formData.subject}
                    onChange={handleChange}
                    className={errors.subject ? "border-red-500" : ""}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-500">{errors.subject ? "Subject is required" : ""}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-1">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="How can we help you?"
                    value={formData.message}
                    onChange={handleChange}
                    error={errors.message}
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-500">{errors.message ? "Message is required" : ""}</p>
                  )}
                </div>
              </div>
              
              {/* reCAPTCHA component */}
              <div className="mt-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                />
                {errors.recaptcha && (
                  <p className="mt-1 text-sm text-red-500">Please complete the CAPTCHA verification</p>
                )}
              </div>
              
              {submitStatus.message && (
                <div className={`py-2 px-3 rounded-md ${
                  submitStatus.type === "success" 
                    ? "bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400" 
                    : "bg-red-100 text-red-800 dark:bg-red-800/20 dark:text-red-400"
                }`}>
                  <p>{submitStatus.message}</p>
                </div>
              )}
              
              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="w-full"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}